import { v4 as uuidv4 } from "uuid";
import {
  CartItemType,
  CartLoyaltyV2SliceType,
} from "../../services/redux/types/CartLoyaltyV2Types";
import { AddLineInput, buildCategoryAddLineInput } from "../cart/cartLineTypes";
import {
  getAllUnavailableItemIds,
  isItemHaveAddons,
} from "../help/itemDetails/itemDetails";
import { canFillFromAvailable } from "../loyalty/offerAvailability";
import {
  EntityImageLookup,
  IsLoyaltyOfferApplicableArgs,
  LoyaltyOfferApplicabilityResult,
  LoyaltyOfferCategory,
  LoyaltyOfferInapplicableReason,
  LoyaltyOfferType,
  LoyaltyTargetConfig,
  LoyaltyTargetType,
} from "../loyaltyTypes";
import { ItemType, SubItemType } from "../types";
import LoyaltyOffer from "./LoyaltyOffer";

export class LoyaltyItemOffer extends LoyaltyOffer {
  // eslint-disable-next-line no-useless-constructor
  constructor(offer: LoyaltyOfferType) {
    super(offer);
  }

  isItemOffer(): boolean {
    return true;
  }

  isItemDiscountOffer(): boolean {
    return this.offerCategory === LoyaltyOfferCategory.ITEM_DISCOUNT;
  }

  isCategoryDiscountOffer(): boolean {
    return this.offerCategory === LoyaltyOfferCategory.CATEGORY_DISCOUNT;
  }

  isItemPointsOffer(): boolean {
    return this.offerCategory === LoyaltyOfferCategory.ITEM_POINTS;
  }

  isCategoryPointsOffer(): boolean {
    return this.offerCategory === LoyaltyOfferCategory.CATEGORY_POINTS;
  }

  hasItemLevelConfig(): boolean {
    return Boolean(this.itemLevelConfig);
  }

  /**
   * Buy side of a BOGO (the "X" the guest pays for), from `validity.requirements`.
   * Bundles have no buy side — every bundle entity is a reward entity.
   */
  getBuyTargetConfig(): LoyaltyTargetConfig | undefined {
    return this.validity?.requirements;
  }

  /** Reward side of a BOGO (the "Y" the guest gets), and the whole of a bundle. */
  getRewardTargetConfig(): LoyaltyTargetConfig | undefined {
    return this.itemLevelConfig;
  }

  hasBuyRequirement(): boolean {
    return Boolean(this.getBuyTargetConfig()?.targetEntities?.length);
  }

  /**
   * BOGO/bundle offers the guest builds themselves on the offer page. Only
   * ITEMS targets are supported — CATEGORIES targets need menu-wide expansion
   * and per-item variant choice, which this flow does not implement, so they
   * are hidden from every surface (see `isSupportedGuestBuiltOffer`).
   */
  isGuestBuiltOffer(): boolean {
    return this.isBogoOffer() || this.isBundleOffer();
  }

  /** The reward is given away outright rather than discounted. */
  isFreeReward(): boolean {
    return (
      (this.isNewValueType() && this.value === 0) ||
      (this.isPercentageValueType() && this.value === 100)
    );
  }

  override hasDeletedItems(entityImageLookup?: EntityImageLookup): boolean {
    if (!entityImageLookup) {
      return false;
    }

    const config = this.itemLevelConfig;
    if (!config?.targetEntities?.length) {
      return false;
    }

    const { targetType, targetEntities } = config;

    const isMissingItemsTarget = (entityId: string): boolean => {
      const entry = entityImageLookup[entityId];

      return !entry?.itemId;
    };

    const isMissingCategoriesTarget = (entityId: string): boolean =>
      !entityImageLookup[entityId];

    const useSome = this.isBundleOffer() || this.isBogoOffer();

    if (targetType === LoyaltyTargetType.ITEMS) {
      return useSome
        ? targetEntities.some((e) => isMissingItemsTarget(e.entityId))
        : targetEntities.every((e) => isMissingItemsTarget(e.entityId));
    }

    if (targetType === LoyaltyTargetType.CATEGORIES) {
      return useSome
        ? targetEntities.some((e) => isMissingCategoriesTarget(e.entityId))
        : targetEntities.every((e) => isMissingCategoriesTarget(e.entityId));
    }

    return false;
  }

  override isOfferApplicable(
    args: IsLoyaltyOfferApplicableArgs
  ): LoyaltyOfferApplicabilityResult {
    const baseResult = super.isOfferApplicable(args);
    if (!baseResult.applicable) {
      return baseResult;
    }

    const { entityImageLookup, itemStatusRedux } = args;
    if (!entityImageLookup || !itemStatusRedux) {
      return baseResult;
    }

    const { allUnavailableItemIds } = getAllUnavailableItemIds(itemStatusRedux);

    /*
     * An entity the menu has never heard of is left to `hasDeletedItems`, so
     * an unknown id counts as available here rather than being judged twice.
     */
    const isAvailable = (entityId: string): boolean => {
      const itemId = entityImageLookup[entityId]?.itemId;

      return itemId === undefined || !allUnavailableItemIds.includes(itemId);
    };

    /*
     * Both sides have to be buildable: a BOGO whose buy side is out of stock
     * is just as impossible as one whose reward side is, and asking only
     * whether *all* of one side had gone let "buy 3 of these 4" advertise
     * itself with two of them missing.
     */
    const buildable =
      canFillFromAvailable(this.getBuyTargetConfig(), isAvailable) &&
      canFillFromAvailable(this.getRewardTargetConfig(), isAvailable);

    if (!buildable) {
      const reason = LoyaltyOfferInapplicableReason.ITEMS_UNAVAILABLE;

      return {
        applicable: false,
        reason,
        descriptor: this.getInapplicabilityDescriptor(reason),
      };
    }

    return baseResult;
  }

  override isItemFromListOffer(): boolean {
    const entities = this.itemLevelConfig?.targetEntities ?? [];

    return (
      entities.length > 1 &&
      entities.every((e) => !e.minimumQuantity && !e.maximumQuantity) &&
      this.itemLevelConfig?.minimumItemCount === 1 &&
      this.itemLevelConfig?.maximumItemCount === 1 &&
      this.offerCategory !== LoyaltyOfferCategory.BUNDLE &&
      this.offerCategory !== LoyaltyOfferCategory.BOGO
    );
  }

  /**
   * V2 item-offer application — returns AddLineInput for direct line push, or
   * null when applyFlow redirect / selectedOfferId must drive UI next.
   */
  applyOffer(
    state: CartLoyaltyV2SliceType,
    menuSubItems: SubItemType[],
    customisedLines?: AddLineInput[]
  ): AddLineInput | null {
    // Category-target offers use AUTO selection on calculate — no client line push.
    if (this.itemLevelConfig?.targetType === LoyaltyTargetType.CATEGORIES) {
      state.loyalty.applyFlow = undefined;

      return null;
    }

    if (menuSubItems.length > 1 && !customisedLines) {
      state.loyalty.applyFlow = {
        selectedOfferId: this.id,
        redirect: {
          loyaltyItemFromListOffer: this.toPlain() as LoyaltyOfferType,
        },
      };

      return null;
    }

    const targetSubItem =
      customisedLines?.[0]?.subItemDetails ?? menuSubItems[0];

    if (
      !customisedLines &&
      isItemHaveAddons(targetSubItem?.allAddonGroups ?? [])
    ) {
      state.loyalty.applyFlow = {
        selectedOfferId: this.id,
        redirect: {
          itemId: targetSubItem.item?.id!,
          subItemId: targetSubItem.id!,
        },
      };

      return null;
    }

    state.loyalty.applyFlow = undefined;

    if (customisedLines?.[0]) {
      return {
        ...customisedLines[0],
        isFreeItem:
          customisedLines[0].isFreeItem ??
          (this.isNewValueType() && this.value === 0),
        discountedItemPercentage:
          customisedLines[0].discountedItemPercentage ??
          (this.isPercentageValueType() ? this.value : undefined),
        lineId: customisedLines[0].lineId ?? uuidv4(),
      };
    }

    const subItem = targetSubItem ?? menuSubItems[0];
    const parentItem = subItem.item as ItemType | undefined;

    return {
      ...buildCategoryAddLineInput(
        "",
        1,
        subItem.price,
        {
          ...subItem,
          allAddonGroups: (subItem.allAddonGroups ?? []).map((ag) => ({
            ...ag,
            addonGroupSubItem: [],
          })),
        },
        {
          id: parentItem?.id ?? subItem.itemId ?? 0,
          img_url: parentItem?.img_url ?? "",
          item_name: parentItem?.item_name ?? subItem.name ?? "",
          uuid: parentItem?.uuid,
          version: parentItem?.version,
        } as ItemType,
        {
          isFreeItem: this.isNewValueType() && this.value === 0,
          discountedItemPercentage: this.isPercentageValueType()
            ? this.value
            : undefined,
        }
      ),
      lineId: uuidv4(),
    };
  }

  /**
   * Legacy cart path (useLoyaltyService === false) — pushes CartItemType into cartActions.
   */
  applyLegacyOffer(
    state: CartLoyaltyV2SliceType,
    menuSubItems: SubItemType[],
    cartItems?: CartItemType[]
  ): CartItemType | null {
    if (menuSubItems.length > 1 && !cartItems) {
      state.loyalty.applyFlow = {
        selectedOfferId: this.id,
        redirect: {
          loyaltyItemFromListOffer: this.toPlain() as LoyaltyOfferType,
        },
      };

      return null;
    }

    const targetSubItem =
      (cartItems?.[0] as CartItemType)?.subItemDetails ?? menuSubItems[0];

    if (!cartItems && isItemHaveAddons(targetSubItem?.allAddonGroups ?? [])) {
      state.loyalty.applyFlow = {
        selectedOfferId: this.id,
        redirect: {
          itemId: targetSubItem.item?.id!,
          subItemId: targetSubItem.id!,
        },
      };

      return null;
    }

    let itemRedeemed: CartItemType;

    if (cartItems?.[0]) {
      itemRedeemed = cartItems[0];
    } else {
      const subItem = targetSubItem ?? menuSubItems[0];
      const parentItem = (subItem as SubItemType & { item?: ItemType }).item;

      itemRedeemed = {
        subItemDetails: {
          ...subItem,
          allAddonGroups: (subItem.allAddonGroups ?? []).map((ag) => ({
            ...ag,
            addonGroupSubItem: [],
          })),
          basePrice: subItem.price,
          previousPrice: -1,
        },
        itemDetails: {
          id: parentItem?.id ?? subItem.itemId ?? 0,
          img_url: parentItem?.img_url ?? "",
          item_name: parentItem?.item_name ?? subItem.name ?? "",
          originalPrice: subItem.price,
          uuid: parentItem?.uuid,
        },
        note: "",
        count: 1,
        totalPrice: subItem.price,
        version: parentItem?.version ?? 1,
        isFreeItem: this.isNewValueType() && this.value === 0,
        isOfferItem: true,
        discountedItemPercentage: this.isPercentageValueType()
          ? this.value
          : undefined,
        isDiscountedItem: !this.isNewValueType() || this.value !== 0,
      } as CartItemType;
    }

    state.loyalty.applyFlow = undefined;

    return itemRedeemed;
  }
}
